import { useEffect, useState } from 'react';

/**
 * ehr#480 (reopened 10-18) - a tab left open across a deploy keeps running the old bundle: Chase's
 * EHR tab still had no Save as draft and no click-to-clear nine hours after both went live. A
 * single-page app never reloads itself, so the tab asks the server now and then which bundle
 * index.html names, and offers a Reload once it's a newer one.
 */

/** The hashed entry bundle a page's HTML loads (`/assets/index-<hash>.js`), or null. */
export function bundleOf(html: string): string | null {
  const m = /<script\b[^>]*\bsrc="([^"]*\/assets\/[^"]+\.js)"/i.exec(html);
  return m ? m[1]! : null;
}

/** The bundle this tab is running, from its own script tag; null in dev (no built bundle). */
export function runningBundle(doc: Pick<Document, 'querySelector'> = document): string | null {
  const el = doc.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  return el?.getAttribute('src') ?? null;
}

/** True when the served page names another bundle than the one running here. */
export function isNewer(running: string | null, served: string | null): boolean {
  return !!running && !!served && served !== running;
}

const EVERY_MS = 10 * 60 * 1000; // a tab in use: every 10 minutes
const MIN_GAP_MS = 60 * 1000; // coming back to the tab: at most once a minute

/** True once a newer EHR has been deployed than the one this tab is running. */
export function useNewVersion(): boolean {
  const [newer, setNewer] = useState(false);
  useEffect(() => {
    const running = runningBundle();
    if (!running) return;
    let last = 0;
    let stop = false;
    const check = () => {
      if (stop || Date.now() - last < MIN_GAP_MS) return;
      last = Date.now();
      fetch('/', { cache: 'no-store', credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!stop && isNewer(running, bundleOf(html))) {
            stop = true;
            setNewer(true);
          }
        })
        .catch(() => undefined); // offline or mid-deploy: try again later
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    const timer = window.setInterval(check, EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    return () => {
      stop = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, []);
  return newer;
}
